import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import subscriptionService from '../services/subscriptionService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_51RMP2oGgetd0muXY5uMb4YkygxholvUPhU7DS43stTYYMtYPXIEe9vZFnPOfGdR0nL6FWH2zjfpiVK4Q8hwocEEH005RvHQPja');

const TopUpForm = ({ tokenType, setTokenType, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // Create payment intent on the backend
      const response = await subscriptionService.topUp(tokenType);
      
      if (!response.client_secret) {
        throw new Error("Failed to get payment configuration from server.");
      }

      // Confirm card payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: confirmError } = await stripe.confirmCardPayment(
        response.client_secret,
        {
          payment_method: {
            card: cardElement,
          }
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        setLoading(false);
        return;
      }

      // Success
      setLoading(false);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to process payment');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          Select the type of tokens you want to purchase. The cost is a flat rate of $10.00.
        </Typography>
        
        <RadioGroup
          value={tokenType}
          onChange={(e) => setTokenType(e.target.value)}
          sx={{ mt: 2 }}
        >
          <Paper variant="outlined" sx={{ p: 2, mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}>
            <FormControlLabel 
              value="llm" 
              control={<Radio />} 
              label={<Typography variant="body1"><strong>100,000 LLM Tokens</strong> for text chat</Typography>} 
              sx={{ m: 0, width: '100%' }}
            />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}>
            <FormControlLabel 
              value="voice" 
              control={<Radio />} 
              label={<Typography variant="body1"><strong>10,000 Voice Tokens</strong> for voice chat</Typography>} 
              sx={{ m: 0, width: '100%' }}
            />
          </Paper>
        </RadioGroup>
        <Typography variant="h6" color="primary" sx={{ mt: 2, textAlign: 'right' }}>
          Total: $10.00
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </Box>

      <DialogActions sx={{ px: 0, pb: 0 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Pay $10.00'}
        </Button>
      </DialogActions>
    </form>
  );
};

const TopUpModal = ({ open, onClose, onSuccess, initialTokenType = 'llm' }) => {
  const [tokenType, setTokenType] = useState(initialTokenType);

  useEffect(() => {
    if (open) {
      setTokenType(initialTokenType);
    }
  }, [open, initialTokenType]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Buy Extra Tokens</DialogTitle>
      <DialogContent>
        <Elements stripe={stripePromise}>
          <TopUpForm 
            tokenType={tokenType} 
            setTokenType={setTokenType} 
            onSuccess={onSuccess} 
            onClose={onClose} 
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
};

export default TopUpModal;
